/* eslint-disable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { SHIFTIQ_API, getAuthHeaders, isAbortError } from "../../api/client";
import { usePgStores } from "../../stores/StoresProvider";
import { locationIdForStore, locationScopeQuery } from "../../lib/storeScope";
import { combineSignals, pageSignal } from "../../routing/pageAbort";
import { Icon, LoadingPulse } from "../../ui";
import { displayName, displayInitial, userEmployeeCode } from "../../lib/userDisplay";
import { WDAYS, WDAYS_GR } from "../../lib/dates";
import { fetchMySoftoneSchedule } from "../../api/softoneSlotSync";
import { useErganiSlots } from "../../hooks";
import { crewWeekHasShift, displaySlotValue, ownSoftoneSlotsByKey, slotKey, slotSoftoneChangeLocked, softoneValueWhenInternalBlank } from "../../lib/softoneSlotSync";
import { scheduleRibbonsVisible } from "../../lib/scheduleSource";
import { FoxlineSlotMarker, FoxlineSlotPopup } from "./FoxlineSlotPopup";
import { ErganiSlotMarker } from "./ErganiSlotMarker";

import { SPECIAL_STATUSES } from "./scheduleHelpers";
import { ScheduleAvailabilityPanel } from "./ScheduleAvailabilityPanel";

export function ScheduleViewerPage({ user, lang }) {
  const { storeNames, storeRecords } = usePgStores();
  const store =
    user.store === "All Stores" || !user.store
      ? (storeNames[0] || "")
      : user.store;
  const days = lang === "gr" ? WDAYS_GR : WDAYS;
  const [weekOffset, setWeekOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [softoneLoading, setSoftoneLoading] = useState(false);
  const [softoneReady, setSoftoneReady] = useState(false);
  const [publishedRows, setPublishedRows] = useState([]);
  const [dayOffOffers, setDayOffOffers] = useState([]);
  const [softoneByKey, setSoftoneByKey] = useState({});
  const employeeCode = userEmployeeCode(user);

  function getMonday(offset) {
    const base = new Date();
    const day  = base.getDay();
    const mon  = new Date(base);
    mon.setDate(base.getDate() - (day === 0 ? 6 : day - 1) + offset * 7);
    mon.setHours(0,0,0,0);
    return mon;
  }
  const monday = getMonday(weekOffset);
  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday); d.setDate(monday.getDate() + i); return d;
  });
  function fmtDate(d) { return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }); }
  function toLocalISO(d) {
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${yr}-${mo}-${day}`;
  }
  const weekStartISO = toLocalISO(monday);

  async function replyToDayOff(offerDate, reply) {
    const res = await fetch(`${SHIFTIQ_API}/schedule/day-off-offers/reply`, {
      method: "POST",
      headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ offer_date: offerDate, reply }),
    });
    if (!res.ok) return;
    setDayOffOffers((rows) => rows.map((row) => (
      row.offer_date === offerDate ? { ...row, reply } : row
    )));
  }

  useEffect(() => {
    if (!employeeCode) {
      setDayOffOffers([]);
      return undefined;
    }
    const local = new AbortController();
    const signal = combineSignals(local.signal, pageSignal());
    fetch(`${SHIFTIQ_API}/schedule/day-off-offers/mine`, { headers: getAuthHeaders(), signal })
      .then((r) => (r.ok ? r.json() : { offers: [] }))
      .then((body) => {
        if (!signal.aborted) setDayOffOffers(body?.offers || []);
      })
      .catch((err) => {
        if (signal.aborted || isAbortError(err)) return;
        setDayOffOffers([]);
      });
    return () => local.abort();
  }, [employeeCode]);

  useEffect(() => {
    const locationId = locationIdForStore(store, storeRecords);
    if (!store || !employeeCode || locationId == null) {
      setPublishedRows([]);
      return;
    }
    const local = new AbortController();
    const signal = combineSignals(local.signal, pageSignal());
    setLoading(true);
    fetch(
      `${SHIFTIQ_API}/schedule?${locationScopeQuery(locationId)}&week_start=${encodeURIComponent(weekStartISO)}&status=published`,
      { headers: getAuthHeaders(), signal }
    )
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        if (signal.aborted) return;
        const mine = (rows || []).filter(
          (row) => String(row.employee_code || "").trim() === String(employeeCode).trim()
        );
        setPublishedRows(mine);
      })
      .catch((err) => {
        if (signal.aborted || isAbortError(err)) return;
        setPublishedRows([]);
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
    return () => local.abort();
  }, [store, weekStartISO, employeeCode, weekOffset, storeRecords]);

  useEffect(() => {
    if (!employeeCode || !weekStartISO) {
      setSoftoneByKey({});
      setSoftoneReady(false);
      return undefined;
    }
    const locationId = locationIdForStore(store, storeRecords);
    const local = new AbortController();
    const signal = combineSignals(local.signal, pageSignal());
    setSoftoneLoading(true);
    setSoftoneReady(false);
    fetchMySoftoneSchedule(weekStartISO, { signal, unifiedLocationId: locationId })
      .then((data) => {
        if (signal.aborted) return;
        setSoftoneByKey(ownSoftoneSlotsByKey(data.slots, data.employee_code || employeeCode));
        setSoftoneReady(Boolean(data.available));
      })
      .catch((err) => {
        if (signal.aborted || isAbortError(err)) return;
        setSoftoneByKey({});
        setSoftoneReady(false);
      })
      .finally(() => {
        if (!signal.aborted) setSoftoneLoading(false);
      });
    return () => local.abort();
  }, [store, weekStartISO, employeeCode, storeRecords]);

  function shiftValueForDay(di) {
    const dateIso = toLocalISO(weekDates[di]);
    const row = publishedRows.find((r) => String(r.rota_date || "").slice(0, 10) === dateIso);
    if (!row) return "";
    if (row.status === "leave") return "ΑΔΕΙΑ";
    if (row.status === "sick") return "ΑΝΑΡΡΩΤΙΚΗ";
    if (row.status === "other_store") return "OTHER_STORE";
    if (row.status === "no_work") return "NO_WORK";
    if (row.is_off) return "OFF";
    if (row.shift_start && row.shift_end) {
      return `${String(row.shift_start).slice(0, 5)}-${String(row.shift_end).slice(0, 5)}`;
    }
    return "";
  }

  function getShift(di) {
    return shiftValueForDay(di);
  }

  const internalByDate = {};
  WDAYS.forEach((_, i) => {
    internalByDate[toLocalISO(weekDates[i])] = shiftValueForDay(i);
  });
  const hasAnyShift = crewWeekHasShift(
    weekDates.map((d) => toLocalISO(d)),
    internalByDate,
    softoneByKey,
    employeeCode
  );
  const [foxlinePopupKey, setFoxlinePopupKey] = useState(null);
  const foxlineOpenSlot = foxlinePopupKey ? softoneByKey[foxlinePopupKey] : null;
  const erganiSync = useErganiSlots({
    store,
    weekStartISO,
    unifiedLocationId: locationIdForStore(store, storeRecords),
    enabled: Boolean(store && employeeCode),
  });
  const [erganiHistory, setErganiHistory] = useState([]);
  useEffect(() => {
    if (!foxlineOpenSlot?.employee_code || !foxlineOpenSlot?.date) {
      setErganiHistory([]);
      return undefined;
    }
    let cancelled = false;
    erganiSync.loadHistory(foxlineOpenSlot.employee_code, foxlineOpenSlot.date)
      .then((rows) => { if (!cancelled) setErganiHistory(rows); })
      .catch(() => { if (!cancelled) setErganiHistory([]); });
    return () => { cancelled = true; };
  }, [foxlineOpenSlot?.employee_code, foxlineOpenSlot?.date, erganiSync.loadHistory]);

  return (
    <div className="anim-fade">
      {/* Employee info card */}
      <div style={{ background: "var(--green)", borderRadius: 18, padding: "18px 20px", marginBottom: 16, display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ width: 48, height: 48, background: "var(--lime)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 18, color: "var(--green)", flexShrink: 0 }}>
          {displayInitial(user)}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: "#fff" }}>{displayName(user)}</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 2 }}>{user.jobTitle} · 📍 {store}</div>
          <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, background: "rgba(200,241,53,0.25)", color: "var(--lime)", fontWeight: 800, padding: "3px 9px", borderRadius: 20, border: "1px solid rgba(200,241,53,0.4)" }}>{user.shiftHours || 8}hr shifts</span>
            <span style={{ fontSize: 10, background: "rgba(200,241,53,0.25)", color: "var(--lime)", fontWeight: 800, padding: "3px 9px", borderRadius: 20, border: "1px solid rgba(200,241,53,0.4)" }}>{user.workDays || 5} days/week</span>
            <span style={{ fontSize: 10, background: "rgba(200,241,53,0.25)", color: "var(--lime)", fontWeight: 800, padding: "3px 9px", borderRadius: 20, border: "1px solid rgba(200,241,53,0.4)", textTransform: "capitalize" }}>{user.group || "service"}</span>
          </div>
        </div>
      </div>

      <ScheduleAvailabilityPanel
        user={user}
        lang={lang}
        store={store}
        storeRecords={storeRecords}
        weekStartISO={weekStartISO}
        weekDates={weekDates}
      />

      {dayOffOffers.length > 0 ? (
        <div style={{ background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 14, padding: "12px 16px", marginBottom: 16 }}>
          <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 6 }}>
            {lang === "gr" ? "Πρόταση άδειας" : "Day off offered"}
          </div>
          {dayOffOffers.map((offer) => {
            const answered = offer.reply === "accepted" || offer.reply === "declined";
            return (
              <div key={offer.offer_date} style={{ fontSize: 13, lineHeight: 1.4, marginTop: 8 }}>
                <div>
                  {lang === "gr"
                    ? `${offer.offer_date}: μπορείς να πάρεις αυτή την ημέρα ως ετήσια άδεια. Υπόλοιπο ${offer.days_left} ημέρες.`
                    : `${offer.offer_date}: you can take this day as annual leave. ${offer.days_left} day${offer.days_left === 1 ? "" : "s"} left.`}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                  <button
                    type="button"
                    onClick={() => replyToDayOff(offer.offer_date, "accepted")}
                    style={{
                      border: "none",
                      borderRadius: 999,
                      padding: "4px 10px",
                      fontWeight: 800,
                      fontSize: 12,
                      cursor: "pointer",
                      background: offer.reply === "accepted" ? "#15803D" : "#fff",
                      color: offer.reply === "accepted" ? "#fff" : "#15803D",
                    }}
                  >
                    {lang === "gr" ? "Ναι" : "Yes"}
                  </button>
                  <button
                    type="button"
                    onClick={() => replyToDayOff(offer.offer_date, "declined")}
                    style={{
                      border: "none",
                      borderRadius: 999,
                      padding: "4px 10px",
                      fontWeight: 800,
                      fontSize: 12,
                      cursor: "pointer",
                      background: offer.reply === "declined" ? "#B91C1C" : "#fff",
                      color: offer.reply === "declined" ? "#fff" : "#B91C1C",
                    }}
                  >
                    {lang === "gr" ? "Όχι" : "No"}
                  </button>
                  {answered ? (
                    <span style={{ fontSize: 12, alignSelf: "center", color: "#92400E" }}>
                      {offer.reply === "accepted"
                        ? (lang === "gr" ? "Είπες ναι" : "You said yes")
                        : (lang === "gr" ? "Είπες όχι" : "You said no")}
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Week navigator */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, padding: "10px 16px" }}>
        <button onClick={() => setWeekOffset(w => w - 1)} style={{ width: 34, height: 34, border: "1.5px solid var(--border)", background: "var(--surface2)", borderRadius: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-secondary)", transition: "all 0.15s" }}>
          <Icon name="back" size={16} />
        </button>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: "var(--text-primary)" }}>
            {fmtDate(weekDates[0])} – {fmtDate(weekDates[6])}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            {weekOffset === 0 ? (lang === "gr" ? "Τρέχουσα εβδομάδα" : "Current week")
             : weekOffset < 0 ? (lang === "gr" ? `${Math.abs(weekOffset)} εβδ. πριν` : `${Math.abs(weekOffset)} week${Math.abs(weekOffset)>1?"s":""} ago`)
             : (lang === "gr" ? `${weekOffset} εβδ. μπροστά` : `${weekOffset} week${weekOffset>1?"s":""} ahead`)}
          </div>
        </div>
        <button onClick={() => setWeekOffset(w => w + 1)} style={{ width: 34, height: 34, border: "1.5px solid var(--border)", background: "var(--surface2)", borderRadius: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-secondary)", transition: "all 0.15s", transform: "rotate(180deg)" }}>
          <Icon name="back" size={16} />
        </button>
      </div>

      {/* Schedule */}
      {(loading || softoneLoading) ? (
        <div style={{ textAlign: "center", padding: 48 }}>
          <LoadingPulse size={28} />
        </div>
      ) : !hasAnyShift ? (
        <div className="empty-state">
          <Icon name="calendar" size={40} />
          <p>{lang === "gr" ? "Το πρόγραμμα δεν έχει δημοσιευτεί ακόμα για αυτή την εβδομάδα" : "Schedule not yet published for this week"}</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {WDAYS.map((day, i) => {
            const val = getShift(i);
            const sp  = val ? SPECIAL_STATUSES.find(s => val === s.value) : null;
            const dateIso = toLocalISO(weekDates[i]);
            const foxSlot = employeeCode ? softoneByKey[slotKey(employeeCode, dateIso)] : null;
            const erganiRow = employeeCode ? erganiSync.byKey[slotKey(employeeCode, dateIso)] : null;
            const softoneFill = softoneValueWhenInternalBlank(foxSlot, val);
            const showRibbons = scheduleRibbonsVisible(softoneFill || val, foxSlot?.foxline, erganiRow);
            return (
              <div
                key={day}
                onClick={() => foxSlot && setFoxlinePopupKey(foxSlot.key)}
                style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "var(--shadow)", position: "relative", cursor: foxSlot && foxSlot.classification && foxSlot.classification !== "idle" && foxSlot.classification !== "in_sync" ? "pointer" : "default" }}
              >
                {showRibbons ? (
                  <FoxlineSlotMarker
                    classification={foxSlot?.classification}
                    internalValue={softoneFill || val}
                    foxlineValue={foxSlot?.foxline}
                    changeLocked={slotSoftoneChangeLocked(foxSlot)}
                    known={softoneReady}
                    lang={lang}
                    onClick={() => foxSlot && setFoxlinePopupKey(foxSlot.key)}
                  />
                ) : null}
                {showRibbons ? (
                  <ErganiSlotMarker
                    internalValue={softoneFill || val}
                    erganiRow={erganiRow}
                    known={!erganiSync.loading && !erganiSync.error}
                    lang={lang}
                    onClick={() => setFoxlinePopupKey(foxSlot?.key || slotKey(employeeCode, dateIso))}
                  />
                ) : null}
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: "var(--text-primary)" }}>{days[i]}</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{fmtDate(weekDates[i])}</div>
                </div>
                {sp
                  ? <span style={{ fontSize: 12, background: sp.color, color: sp.textColor, fontWeight: 800, padding: "5px 14px", borderRadius: 20 }}>{lang === "gr" ? sp.labelGr : sp.label}</span>
                  : softoneFill
                    ? <span style={{ fontSize: 14, fontWeight: 900, color: "#5B21B6", background: "#EDE9FE", padding: "5px 16px", borderRadius: 20 }}>{displaySlotValue(softoneFill, lang)}</span>
                  : !val || val === "OFF"
                    ? <span className="off-tag" style={{ fontSize: 12, padding: "5px 14px" }}>OFF</span>
                    : <span style={{ fontSize: 14, fontWeight: 900, color: "var(--green)", background: "var(--green-light)", padding: "5px 16px", borderRadius: 20 }}>{val}</span>
                }
              </div>
            );
          })}
        </div>
      )}
      <FoxlineSlotPopup
        open={Boolean(foxlineOpenSlot)}
        onClose={() => setFoxlinePopupKey(null)}
        slot={foxlineOpenSlot}
        history={[]}
        lang={lang}
        isHr={false}
        busy={false}
        erganiRow={foxlineOpenSlot ? erganiSync.byKey[foxlineOpenSlot.key] : null}
        erganiHistory={erganiHistory}
      />
    </div>
  );
}

